import React, {useState} from 'react'
import {EntitySelectorModal} from "../EntitySelectorModal";
import Button from "../Button";
import {type EntityType, OptionalSelectedItemsData, SelectedItem as Item, type SelectedItemsData} from "./EntitySelector.types";
import {defaultSelectedItems} from "./EntitySelector.constants";
import {useOperator} from "../../context/OperatorContext";
import {SelectedItemsWrapper} from "./SelectedItemsWrapper";
import {getFirstEntityWithItems} from "../EntitySelectorModal/EntitySelectorModal.helpers";
import {SelectedItem} from "../EntitySelectorModal/SelectedItem";

// Types ----

type EntitySelectorProps = {
  onChange?: (selectedItems: SelectedItemsData) => void
  buttonText: string
  modalTitle: string
  entityTypes?: EntityType[]
  siteId?: number | null
  maxSelectedItems?: number
  initialSelectedItems?: OptionalSelectedItemsData
  disabled?: boolean
}

// Component ----

function EntitySelector({ onChange, modalTitle, buttonText, entityTypes, siteId, maxSelectedItems, initialSelectedItems, disabled = false }: EntitySelectorProps) {
  const {isMultiSite} = useOperator();

  const [showEntitySelectorModal, setShowEntitySelectorModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItemsData>({...defaultSelectedItems, ...initialSelectedItems});

  const selectedItemCount: number = Object.values(selectedItems).reduce((total, items) => total + items.length, 0);
  const firstEntity = getFirstEntityWithItems(selectedItems);

  function handleApprove(items: SelectedItemsData) {
    setShowEntitySelectorModal(false);
    setSelectedItems(items);
    onChange?.(items);
  }

  function handleCancel() {
    setShowEntitySelectorModal(false);
  }

  function handleClearItems() {
    setSelectedItems(defaultSelectedItems);
    onChange?.(defaultSelectedItems);
  }

  function handleRemoveItem<K extends EntityType>(item: Item<K>, entityType: K) {
    const filteredItems = [...selectedItems[entityType]].filter(selectedItem => selectedItem.id !== item.id);
    const newItems = {...selectedItems, [entityType]: filteredItems};

    setSelectedItems(newItems);
    onChange?.(newItems);
  }

  return (
    <div className="flex flex-col gap-4 items-start">
      <Button size={Button.sizes.X_SMALL} color={Button.colors.SECONDARY} onClick={() => setShowEntitySelectorModal(true)} disabled={disabled}>{buttonText}</Button>

      {/* Singular selected item */}
      {(maxSelectedItems === 1 && selectedItemCount > 0 && firstEntity) &&
        <div className='w-full'>
          <SelectedItem entity={firstEntity} onRemoveItem={handleRemoveItem} isMultiSite={isMultiSite} />
        </div>
      }

      {/* Multiple selected items */}
      {maxSelectedItems !== 1 &&
        <div className='w-full'>
          <SelectedItemsWrapper
            selectedItems={selectedItems}
            onRemoveItem={handleRemoveItem}
            isMultiSite={isMultiSite}
            selectedItemCount={selectedItemCount}
            onClearItems={handleClearItems}
          />
        </div>
      }

      <EntitySelectorModal
        title={modalTitle}
        show={showEntitySelectorModal}
        entityTypes={entityTypes}
        onCancel={handleCancel}
        onConfirm={handleApprove}
        siteId={siteId}
        maxSelectedItems={maxSelectedItems}
        initialSelectedItems={selectedItems}
      />
    </div>
  );
}

export {EntitySelector};
